/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useTrackModuleView, useTrackSearch } from "@/lib/activityTracker";
import LangSwitch from "@/components/LangSwitch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, MoreVertical, ArrowLeft, Search, Heart, Baby, Stethoscope,
  AlertTriangle, CalendarClock, LogOut, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GESTATION_DAYS = { Bovins: 283, Ovins: 150, Caprins: 150, Lapins: 31, Autre: 0 };

const TYPE_META = {
  breeding: { icon: Heart, color: "#C84B31", bg: "bg-pink-100", text: "text-pink-800" },
  pregnancy_check: { icon: Stethoscope, color: "#1E6BBA", bg: "bg-blue-100", text: "text-blue-800" },
  birth: { icon: Baby, color: "#1C7A3C", bg: "bg-emerald-100", text: "text-emerald-800" },
  abortion: { icon: AlertTriangle, color: "#8A1F1F", bg: "bg-red-100", text: "text-red-800" },
  weaning: { icon: Sparkles, color: "#7A5C00", bg: "bg-amber-100", text: "text-amber-800" },
};
const TYPES = Object.keys(TYPE_META);

const emptyForm = {
  female_id: "",
  type: "breeding",
  date: new Date().toISOString().slice(0, 10),
  method: "natural",
  partner_tag: "",
  expected_birth_date: "",
  outcome: "pending",
  offspring_count: "",
  offspring_alive: "",
  vet_name: "",
  cost: "",
  notes: "",
};

export default function Reproduction() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const [data, setData] = useState({ records: [], animals: [], stats: { total: 0, by_type: {}, pending_pregnancies: 0 } });
  const [reminders, setReminders] = useState({ upcoming: [], overdue: [] });
  const [loadingData, setLoadingData] = useState(true);
  const [allAnimals, setAllAnimals] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterFemale, setFilterFemale] = useState("all");
  const [openCreate, setOpenCreate] = useState(false);

  useTrackModuleView("reproduction");
  useTrackSearch("reproduction", search);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = async () => {
    setLoadingData(true);
    try {
      const [recs, anims, rems] = await Promise.all([
        axios.get(`${API}/reproduction-events`, { withCredentials: true }),
        axios.get(`${API}/animals`, { withCredentials: true }),
        axios.get(`${API}/reproduction-events/upcoming-births`, { withCredentials: true }),
      ]);
      setData(recs.data);
      setAllAnimals(anims.data.animals || []);
      setReminders(rems.data);
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoadingData(false);
    }
  };

  const animalsById = useMemo(() => {
    const map = {};
    [...allAnimals, ...(data.animals || [])].forEach((a) => { if (a) map[a.id] = a; });
    return map;
  }, [allAnimals, data.animals]);

  const females = useMemo(() => allAnimals.filter((a) => a.sex === "F"), [allAnimals]);

  useEffect(() => {
    if (user && user.role === "client" && user.status === "active") fetchAll();
  }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">{t.app.common.loading}</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  if (user.status !== "active") return <Navigate to="/dashboard/client" replace />;

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/reproduction-events/${deleteTarget.id}`, { withCredentials: true });
      toast.success(t.app.repro.deleted_toast);
      setDeleteTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  const filtered = (data.records || []).filter((r) => {
    if (filterType !== "all" && r.type !== filterType) return false;
    if (filterFemale !== "all" && r.female_id !== filterFemale) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    const an = animalsById[r.female_id];
    return (
      r.partner_tag?.toLowerCase().includes(q) ||
      r.vet_name?.toLowerCase().includes(q) ||
      an?.tag?.toLowerCase().includes(q) ||
      an?.name?.toLowerCase().includes(q)
    );
  });

  const hasFemales = females.length > 0;

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#1C3A27] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard/client" className="flex items-center gap-2" data-testid="back-to-dashboard">
            <ArrowLeft size={18} className="text-white/70" />
            <span className="font-display text-lg font-bold">Mazra3ti</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Button onClick={logout} variant="ghost" size="sm" className="text-white/80" data-testid="repro-logout">
              <LogOut size={16} className="me-2" /> {t.app.common.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
              <Heart size={14} /> {t.app.repro.overline}
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.repro.title}</h1>
            <p className="mt-1 text-[#5C6B5C]">{t.app.repro.subtitle}</p>
          </div>
          {hasFemales ? (
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild>
                <Button data-testid="open-create-repro" className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold">
                  <Plus size={18} className="me-2" /> {t.app.repro.new_event}
                </Button>
              </DialogTrigger>
              <ReproDialogContent
                title={t.app.repro.add_title}
                females={females}
                onClose={() => setOpenCreate(false)}
                onSaved={() => { setOpenCreate(false); fetchAll(); }}
              />
            </Dialog>
          ) : (
            <Link to="/dashboard/client/animals">
              <Button className="h-12 px-5 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold">
                {t.app.repro.add_animal_first}
              </Button>
            </Link>
          )}
        </div>

        {!hasFemales && (
          <div className="mb-8 rounded-2xl border-2 border-dashed border-[#E2E4DF] bg-[#F0F0EB] p-7 text-center text-[#5C6B5C]">
            {t.app.repro.no_females_warning}
          </div>
        )}

        {(reminders.overdue?.length > 0 || reminders.upcoming?.length > 0) && (
          <div className="grid md:grid-cols-2 gap-5 mb-8">
            {reminders.overdue?.length > 0 && (
              <ReminderBlock tone="danger" icon={AlertTriangle} title={`${t.app.repro.reminder_overdue} (${reminders.overdue.length})`} items={reminders.overdue} t={t} />
            )}
            {reminders.upcoming?.length > 0 && (
              <ReminderBlock tone="info" icon={CalendarClock} title={`${t.app.repro.reminder_upcoming} (${reminders.upcoming.length})`} items={reminders.upcoming} t={t} />
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard label={t.app.repro.stat_total} value={data.stats?.total || 0} />
          <StatCard label={t.app.repro.stat_active_pregnancies} value={data.stats?.pending_pregnancies || 0} accent="#C84B31" />
          {TYPES.map((tp) => (
            <StatCard key={tp} label={t.app.repro.types[tp]} value={data.stats?.by_type?.[tp] || 0} accent={TYPE_META[tp].color} />
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]" />
            <Input data-testid="repro-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.app.repro.search_placeholder} className="ps-10 h-11 bg-white border-[#E2E4DF] rounded-xl" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-44 h-11 bg-white border-[#E2E4DF] rounded-xl" data-testid="repro-filter-type">
              <SelectValue placeholder={t.app.repro.th_type} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.app.repro.all_types}</SelectItem>
              {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t.app.repro.types[tp]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterFemale} onValueChange={setFilterFemale}>
            <SelectTrigger className="w-52 h-11 bg-white border-[#E2E4DF] rounded-xl" data-testid="repro-filter-female">
              <SelectValue placeholder={t.app.repro.th_female} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.app.repro.all_females}</SelectItem>
              {females.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.tag}{a.name ? ` — ${a.name}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
          {loadingData ? (
            <div className="p-10 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Heart size={40} className="mx-auto text-[#5C6B5C] opacity-40" />
              <p className="mt-3 text-[#5C6B5C]">
                {data.records.length === 0
                  ? hasFemales
                    ? t.app.repro.empty_first_with_females
                    : t.app.repro.no_females_warning
                  : t.app.repro.empty_search}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.repro.th_date}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.repro.th_female}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.repro.th_type}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.repro.th_method}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.repro.th_partner}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.repro.th_outcome}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.repro.th_due}</th>
                    <th className="text-end px-5 py-3 font-semibold">{t.app.common.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const an = animalsById[r.female_id];
                    const meta = TYPE_META[r.type] || TYPE_META.breeding;
                    const Icon = meta.icon;
                    const methodLabel = r.method ? t.app.repro.methods[r.method] : null;
                    const offspring = r.type === "birth"
                      ? `${r.offspring_alive ?? 0}/${r.offspring_count ?? 0}`
                      : null;
                    return (
                      <tr key={r.id} data-testid={`repro-row-${r.id}`} className="border-t border-[#E2E4DF]">
                        <td className="px-5 py-4 text-[#1C3A27] font-medium">{r.date}</td>
                        <td className="px-5 py-4 text-[#1C3A27]">
                          {an ? <span className="font-mono font-semibold">{an.tag}</span> : <span className="text-[#5C6B5C] italic">—</span>}
                          {an?.name && <span className="text-xs text-[#5C6B5C]"> · {an.name}</span>}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}>
                            <Icon size={12} /> {t.app.repro.types[r.type]}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-[#5C6B5C]">
                          {methodLabel}
                          {offspring && <span className="font-mono font-semibold text-[#1C3A27]">{offspring}</span>}
                          {!methodLabel && !offspring && "—"}
                        </td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{r.partner_tag || "—"}</td>
                        <td className="px-5 py-4">
                          {r.outcome ? <OutcomePill outcome={r.outcome} t={t} /> : "—"}
                        </td>
                        <td className="px-5 py-4 text-[#5C6B5C]">{r.expected_birth_date || "—"}</td>
                        <td className="px-5 py-4 text-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`repro-actions-${r.id}`} className="p-2 rounded-lg hover:bg-[#F0F0EB] text-[#5C6B5C]">
                                <MoreVertical size={18} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setEditTarget(r)} data-testid={`edit-repro-${r.id}`}>
                                {t.app.common.edit}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setDeleteTarget(r)} data-testid={`delete-repro-${r.id}`} className="text-red-700">
                                {t.app.common.delete}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        {editTarget && (
          <ReproDialogContent
            title={t.app.repro.edit_title}
            females={females}
            initial={editTarget}
            onClose={() => setEditTarget(null)}
            onSaved={() => { setEditTarget(null); fetchAll(); }}
          />
        )}
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.app.repro.delete_title}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && t.app.repro.delete_body
                .replace("{type}", t.app.repro.types[deleteTarget.type])
                .replace("{date}", deleteTarget.date)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.app.common.cancel}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-repro" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t.app.common.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, accent = "#1C3A27" }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2E4DF] p-5">
      <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold truncate">{label}</div>
      <div className="font-display text-3xl font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function OutcomePill({ outcome, t }) {
  const styles = {
    pending: "bg-amber-100 text-amber-800",
    confirmed_pregnant: "bg-pink-100 text-pink-800",
    not_pregnant: "bg-zinc-200 text-zinc-700",
    birth_done: "bg-emerald-100 text-emerald-800",
    aborted: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${styles[outcome] || "bg-zinc-200 text-zinc-700"}`}>
      {t.app.repro.outcomes[outcome] || outcome}
    </span>
  );
}

function ReminderBlock({ tone, icon: Icon, title, items, t }) {
  const cls = tone === "danger" ? "border-red-300 bg-red-50" : "border-pink-300 bg-pink-50";
  const iconCls = tone === "danger" ? "text-red-700" : "text-pink-700";
  const titleCls = tone === "danger" ? "text-red-900" : "text-pink-900";
  return (
    <div className={`rounded-2xl border-2 ${cls} p-5`} data-testid={`repro-reminder-${tone}`}>
      <div className={`flex items-center gap-2 font-bold ${titleCls}`}>
        <Icon size={18} className={iconCls} /> {title}
      </div>
      <ul className="mt-3 space-y-2">
        {items.slice(0, 5).map((r) => (
          <li key={r.id} className="flex items-center gap-3 text-sm">
            <span className="font-mono font-semibold text-[#1C3A27]">{r.animal?.tag || "—"}</span>
            <span className="flex-1 text-[#1C3A27] truncate">{r.animal?.name || "—"}</span>
            <span className={`font-semibold ${tone === "danger" ? "text-red-700" : "text-pink-800"}`}>{r.expected_birth_date}</span>
          </li>
        ))}
        {items.length > 5 && (
          <li className="text-xs text-[#5C6B5C] italic">{t.app.repro.reminder_more.replace("{n}", items.length - 5)}</li>
        )}
      </ul>
    </div>
  );
}

function ReproDialogContent({ title, females, initial, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? {
          female_id: initial.female_id,
          type: initial.type,
          date: initial.date,
          method: initial.method || "natural",
          partner_tag: initial.partner_tag || "",
          expected_birth_date: initial.expected_birth_date || "",
          outcome: initial.outcome || "pending",
          offspring_count: initial.offspring_count ?? "",
          offspring_alive: initial.offspring_alive ?? "",
          vet_name: initial.vet_name || "",
          cost: initial.cost ?? "",
          notes: initial.notes || "",
        }
      : { ...emptyForm, female_id: females[0]?.id || "" }
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Auto-compute expected_birth_date when breeding date or female changes (only on create)
  useEffect(() => {
    if (isEdit) return;
    if (form.type !== "breeding") return;
    if (!form.date || !form.female_id) return;
    if (form.expected_birth_date && form._autoFilled === false) return;
    const fem = females.find((a) => a.id === form.female_id);
    const days = GESTATION_DAYS[fem?.species] || 0;
    if (!days) return;
    try {
      const d = new Date(form.date);
      d.setDate(d.getDate() + days);
      const iso = d.toISOString().slice(0, 10);
      setForm((prev) => ({ ...prev, expected_birth_date: iso }));
    } catch (_e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.female_id, form.date, form.type]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.female_id) { setError(t.app.repro.female_required); return; }
    setSubmitting(true);
    const payload = {
      type: form.type,
      date: form.date,
      method: form.type === "breeding" ? form.method : undefined,
      partner_tag: form.partner_tag?.trim() || undefined,
      expected_birth_date: form.expected_birth_date || undefined,
      outcome: form.type === "breeding" ? form.outcome : undefined,
      offspring_count: form.offspring_count === "" ? undefined : parseInt(form.offspring_count, 10),
      offspring_alive: form.offspring_alive === "" ? undefined : parseInt(form.offspring_alive, 10),
      vet_name: form.vet_name?.trim() || undefined,
      cost: form.cost === "" ? undefined : parseFloat(form.cost),
      notes: form.notes?.trim() || undefined,
    };
    if (!isEdit) payload.female_id = form.female_id;
    try {
      if (isEdit) {
        await axios.patch(`${API}/reproduction-events/${initial.id}`, payload, { withCredentials: true });
        toast.success(t.app.repro.saved_toast);
      } else {
        await axios.post(`${API}/reproduction-events`, payload, { withCredentials: true });
        toast.success(t.app.repro.created_toast);
      }
      onSaved?.();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const showBreedingFields = form.type === "breeding";
  const showBirthFields = form.type === "birth" || form.type === "abortion";

  return (
    <DialogContent className="max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} data-testid="repro-form" className="grid grid-cols-2 gap-4 mt-2">
        <div className="col-span-2">
          <Label>{t.app.repro.female_label}</Label>
          <Select
            value={form.female_id}
            onValueChange={(v) => setForm({ ...form, female_id: v })}
            disabled={isEdit}
          >
            <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="repro-female-select">
              <SelectValue placeholder={t.app.repro.female_select_ph} />
            </SelectTrigger>
            <SelectContent>
              {females.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.tag}{a.name ? ` — ${a.name}` : ""} · {t.app.animals.species[a.species] || a.species}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-[#5C6B5C] mt-1">{t.app.repro.female_only}</p>
        </div>
        <div>
          <Label>{t.app.repro.type_label}</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="repro-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t.app.repro.types[tp]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t.app.repro.date_label}</Label>
          <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11 bg-white" data-testid="repro-date" />
        </div>

        {showBreedingFields && (
          <>
            <div>
              <Label>{t.app.repro.method_label}</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="mt-1.5 h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="natural">{t.app.repro.methods.natural}</SelectItem>
                  <SelectItem value="ai">{t.app.repro.methods.ai}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t.app.repro.outcome_label}</Label>
              <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                <SelectTrigger className="mt-1.5 h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t.app.repro.outcomes.pending}</SelectItem>
                  <SelectItem value="confirmed_pregnant">{t.app.repro.outcomes.confirmed_pregnant}</SelectItem>
                  <SelectItem value="not_pregnant">{t.app.repro.outcomes.not_pregnant}</SelectItem>
                  <SelectItem value="birth_done">{t.app.repro.outcomes.birth_done}</SelectItem>
                  <SelectItem value="aborted">{t.app.repro.outcomes.aborted}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>{t.app.repro.partner_tag_label}</Label>
              <Input value={form.partner_tag} onChange={(e) => setForm({ ...form, partner_tag: e.target.value })} placeholder={t.app.repro.partner_ph} className="mt-1.5 h-11 bg-white" />
            </div>
            <div className="col-span-2">
              <Label>{t.app.repro.expected_birth_label}</Label>
              <Input type="date" value={form.expected_birth_date} onChange={(e) => setForm({ ...form, expected_birth_date: e.target.value, _autoFilled: false })} className="mt-1.5 h-11 bg-white" data-testid="repro-expected-birth" />
              <p className="text-xs text-[#5C6B5C] mt-1">{t.app.repro.expected_birth_hint}</p>
            </div>
          </>
        )}

        {showBirthFields && (
          <>
            <div>
              <Label>{t.app.repro.offspring_count_label}</Label>
              <Input type="number" min={0} max={20} value={form.offspring_count} onChange={(e) => setForm({ ...form, offspring_count: e.target.value })} className="mt-1.5 h-11 bg-white" data-testid="repro-offspring-count" />
            </div>
            <div>
              <Label>{t.app.repro.offspring_alive_label}</Label>
              <Input type="number" min={0} max={20} value={form.offspring_alive} onChange={(e) => setForm({ ...form, offspring_alive: e.target.value })} className="mt-1.5 h-11 bg-white" data-testid="repro-offspring-alive" />
            </div>
          </>
        )}

        <div>
          <Label>{t.app.repro.vet_label}</Label>
          <Input value={form.vet_name} onChange={(e) => setForm({ ...form, vet_name: e.target.value })} placeholder={t.app.repro.vet_ph} className="mt-1.5 h-11 bg-white" />
        </div>
        <div>
          <Label>{t.app.repro.cost_label}</Label>
          <Input type="number" min={0} step={0.01} value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="mt-1.5 h-11 bg-white" />
        </div>
        <div className="col-span-2">
          <Label>{t.app.repro.notes_label}</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-white resize-none" placeholder={t.app.repro.notes_ph} />
        </div>

        {error && (
          <div className="col-span-2 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>
        )}
        <DialogFooter className="col-span-2 mt-1">
          <Button type="button" variant="outline" onClick={onClose}>{t.app.common.cancel}</Button>
          <Button type="submit" data-testid="submit-repro" disabled={submitting} className="bg-[#1C3A27] hover:bg-[#142A1C] text-white">
            {submitting ? t.app.common.saving : isEdit ? t.app.common.update : t.app.common.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
